import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api-client";

export interface OperatorStat {
  operatorId: string;
  operatorName: string;
  docsProcessed: number;
  avgDurationMs: number;
  correctionRate: number;
  overrideRate: number;
  // Keying metrics. Hot tier always supplies them; cold tier returns null
  // for operators whose monthly rollups predate keying capture (render "—").
  avgKpm?: number | null;
  avgTimePerFieldMs?: number | null;
  totalKeystrokes?: number | null;
}

export type OperatorPerformanceTier = "hot" | "cold";
export type OperatorPerformanceBucket = "day" | "week" | "month";

export interface OperatorPerformanceData {
  operators: OperatorStat[];
  tier?: OperatorPerformanceTier;
  bucket?: OperatorPerformanceBucket;
}

export function useOperatorPerformance(
  jobTypeId?: string,
  from?: string,
  to?: string,
  bucket?: OperatorPerformanceBucket,
) {
  const [data, setData] = useState<OperatorPerformanceData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (jobTypeId) params.set("jobTypeId", jobTypeId);
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      if (bucket) params.set("bucket", bucket);
      const query = params.toString() ? `?${params.toString()}` : "";
      const result = await api.get<OperatorPerformanceData>(`/analytics/operator-performance${query}`);
      setData(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load operator performance data");
    } finally {
      setIsLoading(false);
    }
  }, [jobTypeId, from, to, bucket]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, isLoading, error, refetch: fetchData };
}
